import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useJourneyHref } from '@/administration/pages/Journey/utils/useJourneyNavigate';
import { JourneyEditViewTeamsResetModal } from '@/features/journey/admin/atoms/JourneyEditViewTeamsResetModal';
import { JourneyEditViewTeamsContent } from '@/features/journey/admin/organisms/JourneyEditViewTeamsContent';
import {
  RLearningJourneySessionDiscussionTeam,
  RUpsertLearningJourneySessionDiscussionTeamMembersInput,
  useUpsertLearningJourneySessionDiscussionTeamMembersMutationRemote,
} from '@/store/v2';
import { useResetLearningJourneyDiscussionTeams } from '@/store/v2/journeys/useResetLearningJourneyDiscussionTeams';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { Layout } from '@/ui/Layout';
import { Button } from '@/ui/Button';

const RESET_SUCCESS_MESSAGE = 'Learning teams reset';

const RESET_ERROR_MESSAGE = 'Something went wrong while resetting learning teams.\nPlease try again later.';

const TitleStyled = styled.div`
  ${({ theme }) => theme.typography.title2};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
  padding-left: 16px;
`;

const TitleSectionStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 40px;
`;

const TitleContainerStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** The snackbar drops a plain `\n`, so the message carries its line break in the markup. */
const SnackbarMessageStyled = styled.span`
  white-space: pre-line;
`;

export const JourneyEditViewTeams = (): React.ReactElement => {
  const navigate = useNavigate();
  const { journeyId, name } = useParams<{ journeyId: string; name: string }>();
  const { discussionsHref } = useJourneyHref();
  const { openSnackbar } = useContext(SharedSnackbarContext);
  // Save and Reset are about changes to a populated set of teams, so neither belongs on the
  // team-size step, and Save has nothing to write until the admin has moved someone.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [teams, setTeams] = useState<RLearningJourneySessionDiscussionTeam[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [upsertTeamMember] = useUpsertLearningJourneySessionDiscussionTeamMembersMutationRemote();
  const { resetTeams, resetLoading } = useResetLearningJourneyDiscussionTeams();

  const arePopulated = teams?.length > 0;

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const handleSaveReordering = async () => {
    const teamMembers: RUpsertLearningJourneySessionDiscussionTeamMembersInput[] = [];

    teams.forEach((team) => {
      team?.members?.forEach((member) => {
        teamMembers.push({ teamId: team?.id as number, userId: member?.user?.id });
      });
    });

    const { data } = await upsertTeamMember({ variables: { sessionId: sessionId as number, teamMembers } });

    if (data?.upsertLearningJourneySessionDiscussionTeamMembers?.success) {
      setHasUnsavedChanges(false);
      openSnackbar({
        message: 'Learning teams updated',
        isDismissive: true,
      });
    } else {
      openSnackbar({
        message: 'Something went wrong',
        type: SnackbarType.DANGER,
        isDismissive: true,
      });
    }
  };

  const handleResetConfirm = async () => {
    const succeeded = await resetTeams(Number(journeyId));

    // A failed reset leaves the teams as they were, so the admin stays where they are with the
    // modal closed behind them — there is nothing to go back to Discussion channels for.
    if (!succeeded) {
      setIsResetModalOpen(false);
      openSnackbar({
        message: <SnackbarMessageStyled>{RESET_ERROR_MESSAGE}</SnackbarMessageStyled>,
        type: SnackbarType.DANGER,
      });

      return;
    }

    setIsResetModalOpen(false);
    // Raised before navigating, but the snackbar lives above the router, so it is still there on
    // Discussion channels — where setting the teams up again starts. Dismissive, or it would stay
    // on every page the admin goes to after that.
    openSnackbar({ message: RESET_SUCCESS_MESSAGE, type: SnackbarType.SUCCESS, isDismissive: true });
    navigate(discussionsHref({ journeyId: Number(journeyId), journeyName: name || '' }));
  };

  return (
    <>
      <Layout.ContentSection marginTop>
        <Button variant="ghost" icon="arrow-left" onClick={handleNavigateBack}>
          Back
        </Button>
        <TitleSectionStyled>
          <TitleContainerStyled>
            <TitleStyled>Learning teams</TitleStyled>
          </TitleContainerStyled>
          {arePopulated && (
            <ActionsStyled>
              <Button variant="secondary" onClick={() => setIsResetModalOpen(true)}>
                Reset
              </Button>
              {hasUnsavedChanges && (
                <Button variant="primary" onClick={handleSaveReordering}>
                  Save
                </Button>
              )}
            </ActionsStyled>
          )}
        </TitleSectionStyled>
      </Layout.ContentSection>
      <Layout.ContentSection marginTop marginBottom>
        <JourneyEditViewTeamsContent
          teams={teams}
          setTeams={setTeams}
          setSessionId={setSessionId}
          onTeamsReordered={() => setHasUnsavedChanges(true)}
        />
      </Layout.ContentSection>
      <JourneyEditViewTeamsResetModal
        open={isResetModalOpen}
        loading={resetLoading}
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={handleResetConfirm}
      />
    </>
  );
};
